import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Trophy, Check, X } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useCelebrate } from "@/components/Celebration";

const LV = { below: "Building", on: "On grade", above: "Above grade" };

export default function LevelUp() {
  const { subjectId } = useParams();
  const celebrate = useCelebrate();
  const [ch, setCh] = useState(null);
  const [i, setI] = useState(0);
  const [fb, setFb] = useState(null);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { http.post(`/levelup/${subjectId}/start`).then((r) => setCh(r.data)).catch((e) => setErr(formatApiError(e))); }, [subjectId]);
  const pick = async (c) => {
    const q = ch.questions[i];
    try {
      const r = await http.post(`/levelup/challenge/${ch.id}/answer`, { qid: q.id, choice: c });
      setFb({ c, ok: r.data.correct });
      setTimeout(() => {
        setFb(null);
        if (!r.data.done) return setI(i + 1);
        setRes(r.data);
        if (r.data.passed) celebrate({ kind: "levelup", subject: ch.subject, subject_id: subjectId, badge: r.data.badge });
      }, 900);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const wrap = (body) => <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14 pb-24" data-testid="levelup-page">{body}</div>;
  if (err) return wrap(<div data-testid="levelup-locked"><p className="font-display text-2xl text-[#2C4A3E]">{err}</p><Link to="/planner" className="inline-block mt-6 px-6 py-3 rounded-full bg-[#2C4A3E] text-white">Back to my plan</Link></div>);
  if (!ch) return wrap(<p className="text-stone-500">Getting your challenge ready…</p>);
  if (res) return wrap(
    <div className="rise text-center" data-testid="levelup-result" data-passed={res.passed}>
      <Trophy className={`w-14 h-14 mx-auto ${res.passed ? "text-[#C87D53]" : "text-stone-400"}`} />
      <h1 className="font-display text-4xl text-[#2C4A3E] mt-4">{res.passed ? `You leveled up in ${ch.subject}!` : "So close. Keep going!"}</h1>
      <p className="text-stone-600 mt-3">{res.passed ? `${LV[ch.from]} → ${LV[ch.to]}. Your lessons will now match your new level.` : `You got ${res.score} of 3. Finish a few more reviews and try again. Every try makes your brain stronger.`}</p>
      <Link to="/planner" data-testid="levelup-back" className="inline-block mt-8 px-6 py-3 rounded-full bg-[#2C4A3E] text-white">Back to my plan</Link>
    </div>
  );
  const q = ch.questions[i];
  return wrap(
    <div className="rise" key={q.id}>
      <p className="text-xs uppercase tracking-widest text-[#C87D53]">Level-Up Challenge · {ch.subject} · {ch.answered + i + 1} of {ch.total}</p>
      <h1 className="font-display text-3xl text-[#2C4A3E] mt-3" data-testid="levelup-question">{q.q}</h1>
      <div className="grid sm:grid-cols-2 gap-3 mt-6">{q.choices.map((c, k) => (
        <button key={k} disabled={!!fb} onClick={() => pick(k)} data-testid={`levelup-choice-${k}`}
          className={`text-left rounded-2xl border px-5 py-4 inline-flex items-center gap-3 transition-colors ${fb?.c === k ? (fb.ok ? "bg-[#E3EBE5] border-[#6B9080]" : "bg-[#FBEAEA] border-red-300") : "bg-white border-[#E6DEC3] hover:border-[#2C4A3E]"}`}>
          {fb?.c === k && (fb.ok ? <Check className="w-4 h-4 text-[#6B9080]" /> : <X className="w-4 h-4 text-red-600" />)}{c}
        </button>
      ))}</div>
      <p className="text-xs text-stone-500 mt-5">Get 2 of 3 right to move up from {LV[ch.from]} to {LV[ch.to]}.</p>
    </div>
  );
}
